import { createWebHistory, createRouter } from 'vue-router';
import appConfig from '@/app.config.json';
import i18next from '@/i18n';
import menuData from '@/json/menu.json';
import store from '@/store';
import { ensureValidAuthSession } from '@/store/auth';
import { waitForFeatureAccessBootstrap } from '@/store/modules/feature-access';

// Router 規則：凡是由 router 直接載入的 Vue 頁面元件，檔名一律使用 *Page.vue 結尾。
const Blank = () => import('@/views/BlankPage.vue');

// ============================================================
// 從 menu.json 建立 breadCrumb 對照表（route-name → category label）
// ============================================================
const LOCALE = 'zh-tw';
const breadcrumbMap = {};
const centerPathMap = {};

const pathToEnLabel = {
	'info-center': 'Information Center',
	event: 'Event Center',
	account: 'My Account',
};

for (const center of menuData.Admin || []) {
	for (const [path, enLabel] of Object.entries(pathToEnLabel)) {
		if (center.label.en === enLabel) {
			centerPathMap[path] = center.label[LOCALE];
		}
	}

	for (const sub of center.sub || []) {
		if (sub['route-name']) {
			breadcrumbMap[sub['route-name']] = center.label[LOCALE];
			continue;
		}

		for (const leaf of sub.sub || []) {
			if (leaf['route-name']) {
				breadcrumbMap[leaf['route-name']] = sub.label[LOCALE];
			}
		}
	}
}

const routes = [
	// menu.json 未出現：系統入口與診斷 route。
	{
		path: '/',
		redirect: '/signin',
	},
	{
		path: '/signin',
		name: 'signin',
		component: () => import('@/views/SignInPage'),
		meta: { layout: 'guest' },
	},
	{
		path: '/signup',
		name: 'signup',
		component: () => import('@/views/SignUpPage.vue'),
		meta: { layout: 'guest', page_title_key: 'signup.title' },
	},
	{
		path: '/signup/verify',
		name: 'signup-verify',
		component: () => import('@/views/SignUpVerifyPage.vue'),
		meta: { layout: 'guest', page_title_key: 'signup.verifyTitle' },
	},
	{
		path: '/signup/complete',
		name: 'signup-complete',
		component: () => import('@/views/SignUpCompletePage.vue'),
		meta: { layout: 'guest', page_title_key: 'signup.completeTitle' },
	},
	{
		path: '/forgot-password',
		name: 'forgot-password',
		component: () => import('@/views/ForgotPasswordPage.vue'),
		meta: {
			layout: 'guest',
			page_title_key: 'passwordRecovery.forgot.title',
		},
	},
	{
		path: '/forgot-password/reset',
		name: 'forgot-password-reset',
		component: () => import('@/views/ResetPasswordPage.vue'),
		meta: {
			layout: 'guest',
			page_title_key: 'passwordRecovery.reset.title',
		},
	},
	{
		path: '/404',
		name: 'not-found',
		component: () => import('@/views/NotFoundPage'),
		meta: { layout: 'guest' },
	},
	{
		path: '/403/:featureKey?/:rejectedRouteName?',
		name: '403',
		component: () => import('@/views/ForbiddenPage.vue'),
		meta: {
			layout: 'auth',
			page_title: '功能尚未開通',
		},
	},
	{
		path: '/dev-verify',
		name: 'dev-verify',
		component: () => import('@/views/DevVerifyPage'),
		meta: { layout: 'guest' },
	},

	// ============================================================
	// 資訊中心 Information Center
	// ============================================================
	{
		path: '/info-center',
		redirect: '/info-center/dashboard',
		children: [
			{
				path: 'dashboard',
				name: 'info-dashboard',
				component: () =>
					import('@/views/Dashboard/crm/DashboardPage.vue'),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '資訊面板',
					featureKey: 'info.dashboard',
				},
			},
			{
				path: 'analytics',
				name: 'analytics',
				component: () =>
					import(
						'@/views/features/statistics-analysis/ActivityAnalyticsReportPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '統計圖表',
					featureKey: 'info.analytics',
				},
			},
			{
				path: 'event-website/:evid?',
				name: 'event-website',
				component: () =>
					import(
						'@/views/features/event-website/EventWebsitePage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '活動網站',
					featureKey: 'info.event-website',
				},
			},
			{
				path: 'notification-history/:evid?',
				name: 'checkin-notification-history',
				component: () =>
					import(
						'@/views/features/notification-history/NotificationRecordsPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '通知記錄',
					page_title_key: 'notification-history.title',
					featureKey: 'event.checkin-notification-history',
				},
			},
			{
				path: 'attendee-journey',
				name: 'attendee-journey',
				component: () =>
					import(
						'@/views/features/attendee-journey/AttendeeJourneyPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '與會者旅程',
					page_title_key: 'attendee-journey.title',
					featureKey: 'info.attendee-journey',
				},
			},
		],
	},

	// ============================================================
	// 活動中心 Event Center
	// ============================================================
	{
		path: '/event',
		redirect: '/event/courses/events',
		children: [
			{
				path: 'courses/events/edit/:evid?',
				name: 'course-events-edit',
				redirect: to => ({
					name: 'course-events',
					params: to.params.evid ? { evid: to.params.evid } : {},
				}),
			},
			{
				path: 'courses/events/:evid?',
				name: 'course-events',
				component: () =>
					import(
						'@/views/features/course-events/EventbasicSettingsWizardPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '活動基本資料',
					featureKey: 'event.basic',
				},
			},
			{
				path: 'courses/event-sections/:evid?',
				name: 'event-section-mgmt',
				component: () =>
					import(
						'@/views/features/event-section-mgmt/EventSectionSettingsPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '活動場次管理',
					featureKey: 'event.sections',
				},
			},
			{
				path: 'courses/invitation-import/:evid?',
				name: 'invitation-import',
				component: () =>
					import(
						'@/views/features/invitation-import/InvitationImportPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '邀請名單匯入',
					featureKey: 'event.invitation-import',
				},
			},
			{
				path: 'courses/invitation-quota/:evid?',
				name: 'invitation-quota-setting',
				component: () =>
					import(
						'@/views/features/invitation-quota/InvitationQuotaSettingPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '邀請名額設定',
					featureKey: 'event.invitation-quota-setting',
				},
			},
			{
				path: 'courses/invitation-list/:evid?',
				name: 'invitation-list-management',
				component: () =>
					import(
						'@/views/features/invitation-list-mgmt/InvitationListMgmtPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '邀請名單管理',
					featureKey: 'event.invitation-list-management',
				},
			},
			{
				path: 'courses/rsvp-send/:evid?',
				name: 'rsvp-send',
				component: () =>
					import('@/views/features/rsvp-send/RsvpSendPage.vue'),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: 'RSVP發送',
					featureKey: 'event.rsvp-send',
				},
			},
			{
				path: 'courses/rsvp-tracking/:evid?',
				name: 'rsvp-tracking',
				component: () =>
					import(
						'@/views/features/rsvp-tracking/RsvpTrackingPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: 'RSVP追蹤',
					featureKey: 'event.rsvp-tracking',
				},
			},
			{
				path: 'courses/registration-list/:evid?',
				name: 'registration-list',
				component: () =>
					import(
						'@/views/features/registration-list-mgmt/RegistrationListMgmtPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '報名名單管理',
					featureKey: 'event.registration-list',
				},
			},
			{
				path: 'courses/registration-review/:evid?',
				name: 'registration-review',
				component: () =>
					import(
						'@/views/features/registration-review/RegistrationReviewPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '報名審核作業',
					featureKey: 'event.registration-review',
				},
			},
			{
				path: 'courses/registration-fee/:evid/:dataid',
				name: 'registration-fee-records',
				component: () =>
					import(
						'@/views/features/course-fee-mgmt/CourseFeeMgmtPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '報名收費管理',
					featureKey: 'event.registration-fee',
				},
			},
			{
				path: 'courses/registration-fee/:evid?',
				name: 'registration-fee-mgmt',
				component: () =>
					import(
						'@/views/features/course-fee-mgmt/CourseFeeMgmtPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '報名收費管理',
					featureKey: 'event.registration-fee',
				},
			},
			{
				path: 'courses/registration-reconciliation/:evid?',
				name: 'registration-reconciliation',
				component: () =>
					import(
						'@/views/features/registration-payment-reconciliation/RegistrationPaymentReconciliationPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '報名收費對帳',
					featureKey: 'event.registration-reconciliation',
				},
			},
			{
				path: 'courses/pre-event-notification/:evid?',
				name: 'course-pre-event-notification',
				component: () =>
					import(
						'@/views/features/course-pre-event-notification/CoursePreEventNotificationPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '通知發送',
					featureKey: 'event.checkin-notification-send',
				},
			},
			{
				path: 'courses/checkin-notification-history/:evid?',
				redirect: route => {
					const { evid: queryEvid, ...query } = route.query;
					return {
						name: 'checkin-notification-history',
						params: {
							evid: route.params.evid || queryEvid || undefined,
						},
						query,
					};
				},
			},
			{
				path: 'courses/checkin-statistics/:evid?',
				name: 'checkin-statistics',
				component: Blank,
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '報到統計',
					featureKey: 'event.checkin-statistics',
				},
			},
			{
				path: 'courses/surveys/manage/:evid?',
				name: 'survey-management',
				component: () =>
					import(
						'@/views/features/post-event-survey/SurveyManagementPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title_key: 'post-event-survey.management.title',
					featureKey: 'badgequest.satisfaction-survey',
				},
			},
			{
				path: 'courses/surveys/results/:evid?',
				name: 'survey-results',
				component: () =>
					import(
						'@/views/features/post-event-survey/SurveyResultsPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title_key: 'post-event-survey.results.title',
					featureKey: 'badgequest.satisfaction-survey',
				},
			},
			{
				path: 'courses/satisfaction/:evid?',
				name: 'course-satisfaction',
				redirect: to => ({
					name: 'survey-management',
					params: to.params.evid ? { evid: to.params.evid } : {},
				}),
			},
		],
	},

	// ============================================================
	// BadgeQuest / 現場互動
	// ============================================================
	{
		path: '/onsite',
		redirect: '/onsite/questions',
		children: [
			{
				path: 'session-checkin/:evid?',
				name: 'session-checkin',
				component: Blank,
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '場次簽到',
					featureKey: 'badgequest.session-checkin',
				},
			},
			{
				path: 'checkin/:evid?',
				name: 'onsite-checkin',
				component: () =>
					import(
						'@/views/features/onsite-checkin/OnsiteCheckinPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '報到名單',
					featureKey: 'event.checkin-list',
				},
			},
			{
				path: 'questions',
				name: 'onsite-questions',
				component: Blank,
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '現場提問',
					featureKey: 'badgequest.onsite-questions',
				},
			},
			{
				path: 'quiz',
				name: 'question-quiz',
				component: Blank,
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '問題搶答',
					featureKey: 'badgequest.quiz',
				},
			},
			{
				path: 'booth-points',
				name: 'booth-points',
				component: Blank,
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '展位集點',
					featureKey: 'badgequest.booth-points',
				},
			},
			{
				path: 'lottery',
				name: 'lottery',
				component: Blank,
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '離場抽獎',
					featureKey: 'badgequest.lottery',
				},
			},
			{
				path: 'voting',
				name: 'agenda-voting',
				component: Blank,
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '議案表決',
				},
			},
		],
	},

	// ============================================================
	// 我的帳戶 My Account
	// ============================================================
	{
		path: '/account',
		redirect: '/account/settings',
		children: [
			{
				path: 'settings',
				name: 'settings-management',
				component: () =>
					import(
						'@/views/features/account-settings/AccountSettingsPage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '設定管理',
					featureKey: 'account.settings',
				},
			},
			{
				path: 'organization',
				name: 'event-organization',
				component: () =>
					import(
						'@/views/features/org-profile/OrganizationProfilePage.vue'
					),
				meta: {
					layout: 'auth',
					domain: 'Admin',
					page_title: '活動單位',
					featureKey: 'account.organization',
				},
			},
		],
	},

	// 登出由 menu.json 連結；fallback route 未列於 menu.json。
	{
		path: '/signout',
		name: 'signout',
		component: () => import('@/views/SignOutPage'),
		meta: { layout: 'guest' },
	},
	{
		path: '/:pathMatch(.*)*',
		redirect: '/404',
	},
];

const routerBase = process.env.BASE_URL || '/';

const router = createRouter({
	history: createWebHistory(routerBase),
	routes,
});

const buildSigninRedirect = to => ({
	name: 'signin',
	query: { redirect: to.fullPath },
});

const buildForbiddenRedirect = to => ({
	name: '403',
	params: to?.meta?.featureKey
		? {
				featureKey: to.meta.featureKey,
				rejectedRouteName: String(to.name || ''),
		  }
		: undefined,
});

// 導航守衛：自動從 menu.json 設定 breadCrumb，並在 feature route 進入前做 server-side page check。
router.beforeEach(async to => {
	let breadCrumb = '';

	for (const record of to.matched) {
		const pathPrefix = record.path.replace(/^\//, '').split('/')[0];
		if (!record.name && centerPathMap[pathPrefix]) {
			breadCrumb = centerPathMap[pathPrefix];
		}
		if (record.name && breadcrumbMap[record.name]) {
			breadCrumb = breadcrumbMap[record.name];
		}
	}

	to.meta.breadCrumb = breadCrumb;

	// signin/signup/signout/forgot-password/reset/403/404/fallback/dev-verify 與 agenda-voting
	// 沒有 featureKey，因此不會進入 Feature Access guard。
	const featureKey = to.meta?.featureKey;
	if (!featureKey) return true;

	try {
		// 等待 App 完成原有 createServer() 與 menu bootstrap，避免初次
		// deep link 在 API client 尚未初始化前送出 page check。
		await waitForFeatureAccessBootstrap();

		const auth = store.state.settings.auth;
		if (!(await ensureValidAuthSession(store))) {
			return buildSigninRedirect(to);
		}

		const ogzuid = auth.accessOUID;
		if (!ogzuid) return buildForbiddenRedirect(to);

		// 每次進入功能頁都更新方案目錄，讓方案升降級後的選單同步更新。
		await store.dispatch('featureAccess/loadMenu', { ogzuid });

		// 每一次 feature route navigation 都重新向 backend check；Vuex map
		// 只供 menu 顯示，不能作為最終授權依據。
		const allowed = await store.dispatch('featureAccess/checkFeature', {
			ogzuid,
			featureKey,
		});

		return allowed && store.state.featureAccess.catalogLoaded
			? true
			: buildForbiddenRedirect(to);
	} catch (error) {
		if (error?.response?.status === 401) {
			return buildSigninRedirect(to);
		}

		// FEATURE_NOT_ENABLED 與 fail-closed 的 catalog/system error 都
		// 不放行 feature route；403 頁本身沒有 featureKey，不會遞迴觸發 guard。
		return buildForbiddenRedirect(to);
	}
});

router.afterEach(to => {
	const appTitle = appConfig.title || 'YouEvent365 Organizer';
	const pageTitle = to.meta?.page_title_key
		? i18next.t(to.meta.page_title_key)
		: to.meta?.page_title;
	document.title = pageTitle ? `${pageTitle} - ${appTitle}` : appTitle;
});

export default router;
